import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Popover } from 'react-tiny-popover';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';

import Button from '../../../../components/Button';
import Input from '../../../../components/Form/Input';
import Select from '../../../../components/Form/Select';
import { employmentStatus } from '../../../../utils/selectOptions';
import { maskCurrency, unmaskCurrency } from '../../../../utils/masks';
import { nextAction } from '../../../../saga/actions/workflow';
import { getHTTPErrorMessage } from '../../../../utils/fetch';
import { getNextUrl } from '../url';

function Step6(props) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const errorMsg = useMemo(() => getHTTPErrorMessage(props.workflow.error), [props.workflow]);

  // get functions to build form with useForm() hook
  const hookForm = useForm({ reValidateMode: 'onSubmit' });
  const { handleSubmit, setValue, getValues, clearErrors } = hookForm;

  // set initial values
  const grossMonthlyIncome = _.get(props.workflow, 'data.data.grossMonthlyIncome');
  const employmentStatus1 = _.get(props.workflow, 'data.data.employmentStatus');
  const employerName = _.get(props.workflow, 'data.data.employerName');
  useEffect(() => {
    setValue('grossMonthlyIncome', grossMonthlyIncome);
    setValue('employmentStatus', employmentStatus1);
    setValue('employerName', employerName);
  }, [grossMonthlyIncome, employmentStatus1, employerName, setValue]);

  // handle functions
  const onSubmit = (data) => {
    props.nextAction({
      url: getNextUrl(),
      method: 'PUT',
      data: {
        GrossMonthlyIncome: unmaskCurrency(data.grossMonthlyIncome),
        EmploymentStatus: data.employmentStatus,
        EmployerName: data.employerName.trim(),
      },
    });
    return false;
  };
  function validateEmployerName(name) {
    const status = getValues('employmentStatus');
    if (status === 'unemployed' || status === 'student' || status === 'retired') {
      return null;
    }
    if (!name || !name.trim().length) {
      return 'Employer Name is required';
    }
    return null;
  }
  function validateAmount(val) {
    const value = unmaskCurrency(val);
    if (!value) {
      return 'This input field is required';
    } if (value < 0 || value > 41600) {
      return 'The amount must be between $0 and $41,600';
    }
    return null;
  }
  const handleStatusChange = () => {
    clearErrors('employerName');
    clearErrors('employmentStatus');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center md:text-left font-bold text-[26px] leading-[26px] md:text-[32px] md:leading-[38px] text-[#263952]">
        We need some
        {' '}
        <br />
        {' '}
        personal details from you.
      </div>
      <div className="text-center md:text-left font-normal text-[15px] leading-[20px] text-[#263952] mt-[8px] ">
        This won’t affect your credit score
        {' '}
        <sup>1</sup>
      </div>
      <div className="text-center md:text-left font-medium text-[18px] leading-[32px] text-black mt-[30px]">
        Please tell us your income.
      </div>
      <div className="relative">
        <Input
          autoFocus
          name="grossMonthlyIncome"
          label="Gross Monthly Income"
          placeholder="$ "
          className="w-full md:w-2/5"
          isMasked={maskCurrency}
          hookForm={hookForm}
          validate={validateAmount}
        />
        <div className="absolute top-0">
          <Popover
            isOpen={isPopoverOpen}
            positions={['top']} // preferred positions by priority
            align="start"
            padding={2}
            onClickOutside={() => setIsPopoverOpen(false)}
            containerClassName="mt-[-8px] ml-[0px] md:ml-[-18px]"
            content={(
              <>
                <div className="rounded-md border border-[#00214732] p-5 bg-[#ffffff] m-auto max-w-[90%] md:max-w-[400px] max-h-[500px] overflow-y-auto text-[#3f556e] text-[14px] scrollbar">
                  Your total verifiable gross income might include your salary, retirement income or other sources of income.
                  <br />
                  You don&apos;t have to include alimony, child support, or separate maintenance income unless you want it considered as a basis for repayment.
                  <br />
                  Your income and/or employment is subject to verification.
                </div>
                <span className="arrow left-2 hidden md:block" />
              </>
            )}
          >
            <div className="absolute top-[-4px] left-[150px] text-center w-[20px] h-[20px] rounded-[20px] bg-[#87cefa] leading-[20px] cursor-pointer" onClick={() => setIsPopoverOpen(!isPopoverOpen)}>?</div>
          </Popover>
        </div>
      </div>
      <div className="md:flex w-full md:w-11/12">
        <div className="w-full md:w-2/5">
          <Select
            name="employmentStatus"
            label="Employment Status"
            data={employmentStatus}
            hookForm={hookForm}
            registerOptions={{
              required: 'Employment Status is required',
            }}
            onChange={handleStatusChange}
          />
        </div>
        <div className="ml-[0px] w-full md:w-3/5 md:ml-[10px]">
          <Input
            name="employerName"
            label="Employer Name"
            isMasked={false}
            hookForm={hookForm}
            registerOptions={{
              validate: validateEmployerName,
            }}
          />
        </div>
      </div>
      <Button isLoading={props.workflow.isLoading} type="submit" className="w-full md:w-[240px]">Continue</Button>
      {errorMsg && <div className="mt-3 mb-3 text-[#d71f28] text-xs">{errorMsg}</div>}
    </form>
  );
}

Step6.propTypes = {
  workflow: PropTypes.object.isRequired,
  nextAction: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => ({
      workflow: state.workflow,
    }),
    {
      nextAction,
    }
  )
)(Step6);
